// src/components/AppContent.tsx - OPTIMIZADO CON CODE SPLITTING
import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Hook de autenticación
import { useAuth } from '../contexts/AuthContext';

// Layouts - Mantener importación estática (son necesarios siempre)
import SuperadminLayout from './Layout/SuperadminLayout';
import GymLayout from './Layout/GymLayout';

// Páginas de autenticación - Mantener estáticas (son el punto de entrada)
import Login from '../pages/auth/Login';
import Register from '../pages/auth/Register';

// ✅ CODE SPLITTING - Lazy load de todas las páginas
// Páginas del superadmin
const SuperadminDashboard = lazy(() => import('../pages/superadmin/Dashboard'));
const GymsManager = lazy(() => import('../pages/superadmin/Gyms'));
const SubscriptionsManager = lazy(() => import('../pages/superadmin/Subscriptions'));
const RevenueManager = lazy(() => import('../pages/superadmin/Revenue'));
const GymAccountDetails = lazy(() => import('../pages/superadmin/GymAccountDetails'));
const GlobalExercises = lazy(() => import('../pages/superadmin/GlobalExercises'));
const PendingPayments = lazy(() => import('../pages/superadmin/PendingPayments'));
const LandingSettings = lazy(() => import('../pages/superadmin/LandingSettings'));

// Páginas del gimnasio
const Dashboard = lazy(() => import('../pages/dashboard/Dashboard'));
const DashboardImproved = lazy(() => import('../pages/dashboard/DashboardImproved'));
const Members = lazy(() => import('../pages/members/Members'));
const Attendance = lazy(() => import('../pages/attendance/Attendance'));
const Kiosk = lazy(() => import('../pages/attendance/Kiosk'));
const Cashier = lazy(() => import('../pages/cashier/Cashier'));
const Reports = lazy(() => import('../pages/reports/Reports'));
const BusinessProfile = lazy(() => import('../pages/settings/BusinessProfile'));
const Memberships = lazy(() => import('../pages/settings/Memberships'));
const Activities = lazy(() => import('../pages/settings/Activities'));
const Users = lazy(() => import('../pages/settings/Users'));
const Exercises = lazy(() => import('../pages/exercises/Exercises'));
const Routines = lazy(() => import('../pages/routines/Routines'));
const MemberRoutines = lazy(() => import('../pages/member-routines/MemberRoutines'));
const UnifiedMembershipPayments = lazy(() => import('../components/payments/UnifiedMembershipPayments'));

// Páginas públicas y admin
const UnifiedRegistration = lazy(() => import('../pages/public/UnifiedRegistration'));
const PendingRegistrations = lazy(() => import('../pages/admin/PendingRegistrations'));
const QRCodeGenerator = lazy(() => import('../pages/admin/QRCodeGenerator'));
const HelpCenter = lazy(() => import('../pages/help/HelpCenter'));
const ForgotPassword = lazy(() => import('../pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('../pages/auth/ResetPassword'));
const PrivacyPolicy = lazy(() => import('../pages/public/PrivacyPolicy'));

const MemberUpdateTrackerOptimized = lazy(() => import('../pages/admin/MemberUpdateTrackerOptimized'));
const MembershipAssignmentControlOptimized = lazy(() => import('../pages/admin/MembershipAssignmentControlOptimized'));
const Birthdays = lazy(() => import('../pages/birthdays/Birthdays'));
const Products = lazy(() => import('../pages/products/Products'));
const ClassesPage = lazy(() => import('../pages/classes/ClassesPage').then(module => ({ default: module.ClassesPage })));
const PaymentCallback = lazy(() => import('../pages/subscription/PaymentCallback'));
const AccountDeletion = lazy(() => import('../pages/public/AccountDeletion'));
const MemberHome = lazy(() => import('../pages/portal/MemberHome'));
const MemberQR = lazy(() => import('../pages/portal/MemberQR'));
const MemberRoutinesPortal = lazy(() => import('../pages/portal/MemberRoutines'));
const MemberAttendances = lazy(() => import('../pages/portal/MemberAttendances'));
const MemberPayments = lazy(() => import('../pages/portal/MemberPayments'));
const MemberClasses = lazy(() => import('../pages/portal/MemberClasses'));
const MemberProfilePortal = lazy(() => import('../pages/portal/MemberProfile'));
const MemberBodyMeasurements = lazy(() => import('../components/portal/MemberBodyMeasurements'));
const MemberLogin = lazy(() => import('../pages/auth/MemberLogin'));
const MemberLayout = lazy(() => import('./Layout/MemberLayout'));

// Componente de loading para Suspense
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <span className="text-gray-600">Cargando...</span>
    </div>
  </div>
);

// Componente de protección de rutas
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  requiredRole?: 'superadmin' | 'admin' | 'user' | 'member';
  allowedRoles?: Array<'superadmin' | 'admin' | 'user' | 'member'>;
}> = ({ children, requiredRole, allowedRoles }) => {
  const { currentUser, userRole, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Verificar rol específico requerido
  if (requiredRole && userRole !== requiredRole) {
    if (userRole === 'superadmin') {
      return <Navigate to="/superadmin/dashboard" replace />;
    } else if (userRole === 'member') {
      return <Navigate to="/portal" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Verificar roles permitidos
  if (allowedRoles && !allowedRoles.includes(userRole as any)) {
    if (userRole === 'superadmin') {
      return <Navigate to="/superadmin/dashboard" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

// 🆕 Componente de protección por módulo de suscripción
const ModuleProtectedRoute: React.FC<{
  children: React.ReactNode;
  moduleId: string;
}> = ({ children, moduleId }) => {
  const { hasModule, loading, userRole } = useAuth();

  if (loading) return <PageLoader />;

  // Superadmin siempre tiene acceso
  if (userRole === 'superadmin') return <>{children}</>;

  if (!hasModule(moduleId as any)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8">
        <div className="text-5xl mb-4">🔒</div>
        <h2 className="text-xl font-semibold text-gray-700 mb-2">Módulo no disponible</h2>
        <p className="text-gray-500 max-w-sm">
          Esta función no está incluida en tu plan actual.
          Contactá al administrador para más información.
        </p>
      </div>
    );
  }

  return <>{children}</>;
};

// Componente principal de contenido
const AppContent: React.FC = () => {
  const { currentUser, userRole, loading } = useAuth();
  const location = useLocation();

  // Redireccionamiento automático basado en rol
  useEffect(() => {
    if (!loading && currentUser && location.pathname === '/') {
      if (userRole === 'superadmin') {
        window.location.replace('/superadmin/dashboard');
      } else if (userRole === 'member') {
        window.location.replace('/portal');
      } else {
        window.location.replace('/dashboard');
      }
    }
  }, [currentUser, userRole, loading, location.pathname]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <span className="text-gray-600">Iniciando aplicación...</span>
          {currentUser && (
            <div className="mt-2 text-sm text-gray-500">
              Cargando datos para {currentUser.email}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Rutas públicas */}
        <Route
          path="/login"
          element={
            currentUser ? (
              userRole === 'superadmin' ? (
                <Navigate to="/superadmin/dashboard" replace />
              ) : userRole === 'member' ? (
                <Navigate to="/portal" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            ) : (
              <Login />
            )
          }
        />

        <Route
          path="/register"
          element={
            currentUser ? (
              userRole === 'superadmin' ? (
                <Navigate to="/superadmin/dashboard" replace />
              ) : userRole === 'member' ? (
                <Navigate to="/portal" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            ) : (
              <Register />
            )
          }
        />

        {/* ✅ RUTA PÚBLICA UNIFICADA - Registro y Actualización */}
        <Route
          path="/register/:gymId"
          element={<UnifiedRegistration />}
        />

        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route
          path="/acceso"
          element={
            currentUser ? (
              userRole === 'member' ? (
                <Navigate to="/portal" replace />
              ) : userRole === 'superadmin' ? (
                <Navigate to="/superadmin/dashboard" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            ) : (
              <MemberLogin />
            )
          }
        />
        <Route path="/delete-account" element={<AccountDeletion />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/payment/callback" element={<PaymentCallback />} />

        {/* Portal de Socios (Mobile Optimized) */}
        <Route path="/portal/*" element={
          <ProtectedRoute requiredRole="member">
            <Suspense fallback={<PageLoader />}>
              <MemberLayout>
                <Routes>
                  <Route path="" element={<MemberHome />} />
                  <Route path="qr" element={<MemberQR />} />
                  <Route path="routines" element={<MemberRoutinesPortal />} />
                  <Route path="measurements" element={<MemberBodyMeasurements />} />
                  <Route path="attendance" element={<MemberAttendances />} />
                  <Route path="payments" element={<MemberPayments />} />
                  <Route path="classes" element={<MemberClasses />} />
                  <Route path="profile" element={<MemberProfilePortal />} />
                  <Route path="*" element={<Navigate to="" replace />} />
                </Routes>
              </MemberLayout>
            </Suspense>
          </ProtectedRoute>
        } />

        {/* Rutas del superadmin */}
        <Route path="/superadmin/*" element={
          <ProtectedRoute requiredRole="superadmin">
            <SuperadminLayout>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="dashboard" element={<SuperadminDashboard />} />
                  <Route path="gyms" element={<GymsManager />} />
                  <Route path="subscriptions" element={<SubscriptionsManager />} />
                  <Route path="revenue" element={<RevenueManager />} />
                  <Route path="gym-account" element={<GymAccountDetails />} />
                  <Route path="exercises" element={<GlobalExercises />} />
                  <Route path="pending-payments" element={<PendingPayments />} />
                  <Route path="landing" element={<LandingSettings />} />
                  <Route path="" element={<Navigate to="dashboard" replace />} />
                </Routes>
              </Suspense>
            </SuperadminLayout>
          </ProtectedRoute>
        } />

        {/* Kiosco de Asistencia (Pantalla Completa, requiere sesión) */}
        <Route path="/kiosk" element={
          <ProtectedRoute allowedRoles={['admin', 'user']}>
            <ModuleProtectedRoute moduleId="attendance">
              <Suspense fallback={<PageLoader />}>
                <Kiosk />
              </Suspense>
            </ModuleProtectedRoute>
          </ProtectedRoute>
        } />

        {/* Rutas del gimnasio */}
        <Route path="/*" element={
          <ProtectedRoute allowedRoles={['admin', 'user']}>
            <GymLayout>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Dashboards */}
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="dashboard-financial" element={
                    <ModuleProtectedRoute moduleId="reports">
                      <DashboardImproved />
                    </ModuleProtectedRoute>
                  } />

                  {/* Gestión */}
                  <Route path="members" element={<Members />} />
                  <Route path="attendance" element={
                    <ModuleProtectedRoute moduleId="attendance">
                      <Attendance />
                    </ModuleProtectedRoute>
                  } />

                  {/* Finanzas - Solo admins */}
                  <Route path="cashier" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <ModuleProtectedRoute moduleId="daily_cash">
                        <Cashier />
                      </ModuleProtectedRoute>
                    </ProtectedRoute>
                  } />
                  <Route path="reports" element={
                    <ModuleProtectedRoute moduleId="reports">
                      <Reports />
                    </ModuleProtectedRoute>
                  } />

                  {/* ✅ Membresías y Cobros (Módulo Unificado) */}
                  <Route path="payments" element={
                    <ProtectedRoute allowedRoles={['admin', 'user']}>
                      <UnifiedMembershipPayments />
                    </ProtectedRoute>
                  } />

                  {/* Entrenamiento */}
                  <Route path="exercises" element={
                    <ModuleProtectedRoute moduleId="routines">
                      <Exercises />
                    </ModuleProtectedRoute>
                  } />
                  <Route path="routines" element={
                    <ModuleProtectedRoute moduleId="routines">
                      <Routines />
                    </ModuleProtectedRoute>
                  } />
                  <Route path="member-routines" element={
                    <ModuleProtectedRoute moduleId="routines">
                      <MemberRoutines />
                    </ModuleProtectedRoute>
                  } />

                  {/* Configuración - Solo admins */}
                  <Route path="settings/business" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <BusinessProfile />
                    </ProtectedRoute>
                  } />
                  <Route path="settings/memberships" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <Memberships />
                    </ProtectedRoute>
                  } />
                  <Route path="settings/activities" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <Activities />
                    </ProtectedRoute>
                  } />
                  <Route path="settings/users" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <Users />
                    </ProtectedRoute>
                  } />

                  <Route path="help" element={<HelpCenter />} />

                  {/* ✅ NUEVAS RUTAS - Auto-registro */}
                  <Route path="pending-registrations" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <ModuleProtectedRoute moduleId="qr_registration">
                        <PendingRegistrations />
                      </ModuleProtectedRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="qr-generator" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <ModuleProtectedRoute moduleId="qr_registration">
                        <QRCodeGenerator />
                      </ModuleProtectedRoute>
                    </ProtectedRoute>
                  } />

                  <Route
                    path="/update-tracker"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <MemberUpdateTrackerOptimized />
                      </ProtectedRoute>
                    }
                  />

                  <Route path="/birthdays" element={<Birthdays />} />
                  <Route path="/products" element={
                    <ModuleProtectedRoute moduleId="products">
                      <Products />
                    </ModuleProtectedRoute>
                  } />
                  <Route path="/classes" element={
                    <ModuleProtectedRoute moduleId="classes">
                      <ClassesPage />
                    </ModuleProtectedRoute>
                  } />

                  <Route
                    path="/membership-control"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'user']}>
                        <UnifiedMembershipPayments />
                      </ProtectedRoute>
                    }
                  />

                  {/* Ruta por defecto */}
                  <Route path="/" element={<Navigate to="dashboard" replace />} />
                </Routes>
              </Suspense>
            </GymLayout>
          </ProtectedRoute>
        } />
      </Routes>
    </Suspense>
  );
};

export default AppContent;
